/**
 * The entry screens' and the account's words: log in, create an account, the name steps, character select, the
 * returning prompt, the logout dialogs, the creation's Look step, the voice choice, the page's own sentences for a
 * request that got no usable answer, and `refused`, the sentences the gateway's account endpoints answer a refusal
 * with. Sentence case, a sentence with a full stop and a title, a button or a label with none (ADR-061's amendment
 * "every reply in sentence case"). The log in, register and one-field name step wording is the page's from before
 * the kit; the family step's and the name step's family name and first name are drafts for the author (ADR-065
 * decision 3); "Keep me logged in", the returning prompt's words and the logout retry's are approved (ADR-075's
 * note of 2026-09-28); "Confirm password" and the reveal button's two names are drafts (P1.9-T92); character
 * select's are the design's of 2026-10-01, drafts for the author.
 *
 * `refused` is one set for the page and the gateway: the gateway sends a value as `message`, the page words the
 * same value from the refusal's `error` where the code has one sentence, and the page's own check of a field
 * before sending reads the gateway's value for that field. `fields` holds the two the gateway never answers.
 * `{status}` is an HTTP status; `{used}` and `{limit}` are counts of character slots, printed as digits.
 */
export const ACCOUNT = {
  login: { title: "Log in", submit: "Log in", switch: "Create an account", busy: "Logging in" },
  register: { title: "Create an account", submit: "Create account", switch: "I already have an account", busy: "Creating the account" },
  naming: { title: "Name your character", next: "Continue", switch: "Log out", busy: "Naming your character" },
  family: { title: "Name your family", submit: "Enter the world", switch: "Log out", busy: "Naming your family" },
  labels: { email: "Email", password: "Password", confirm: "Confirm password", characterName: "Character name", firstName: "First name", familyName: "Family name" },
  everywhere: "Log out everywhere",
  keep: "Keep me logged in",
  reveal: { show: "Show password", hide: "Hide password" },
  registered: "Account created, now log in.",
  namingNote: "Other players see this name.",
  familyNote: "All your characters share it.",
  fields: {
    confirm: "Passwords do not match.",
    characterName: "That character name is not acceptable.",
  },
  request: {
    unreachable: "The server did not answer.",
    unsent: "This page could not send the request.",
    failed: "That did not work ({status}).",
  },
  select: {
    title: "Choose a character",
    list: "Characters",
    empty: "This account has no characters yet.",
    play: "Play",
    busy: "Entering the world",
    account: "Account",
    logOutTitle: "Log out?",
    logOutBody: "You return to the login screen.",
    newCharacter: "New character",
    slots: "{used} of {limit} slots used",
    full: "All {limit} character slots are in use.",
    back: "Back",
  },
  returning: {
    title: "Welcome back",
    loggedInAs: "Logged in as",
    continue: "Continue",
    busy: "Logging out",
  },
  logoutEverywhere: {
    title: "Log out everywhere?",
    body: "Every tab and device logged in to this account must log in again with the email and password, this one included.",
    confirm: "Log out everywhere",
    cancel: "Cancel",
    lost: "This tab was already logged out, so no other tab or device was logged out. Log in again to log out everywhere.",
    lostAction: "Log in",
  },
  logoutRetry: {
    title: "Logout not finished",
    body: "The server did not confirm the logout, so this device may still be logged in.",
    retry: "Retry",
    leave: "Leave anyway",
  },
  lookStep: {
    title: "Create a character: look",
    back: "Back",
    randomise: "Randomise",
    primary: "Enter the world",
    saving: "Saving",
  },
  voice: {
    label: "Voice",
    preview: "Preview",
    noSample: "That voice has no sample yet.",
  },
  refused: {
    credentials: "Those details do not match an account.",
    busy: "The server is busy; try again in a moment.",
    throttled: "Too many login attempts for those details; try again in a minute.",
    sessionGone: "That session is no longer valid.",
    emailTaken: "That email is already registered.",
    accountNameTaken: "That account name is taken.",
    registrationClashed: "That registration clashed with another; try again.",
    emailInvalid: "That email is not acceptable.",
    accountNameInvalid: "That account name is not acceptable.",
    passwordInvalid: "That password is not acceptable.",
    firstNameInvalid: "That first name is not acceptable.",
    familyNameInvalid: "That family name is not acceptable.",
    familyReserved: "That family name is reserved for the story.",
    fullNameReserved: "That full name is reserved for the story.",
    familyStaffWord: "That family name holds a word kept for the staff.",
    firstNameStaffWord: "That first name holds a word kept for the staff.",
    familyNeeded: "The game has changed; reload the page to name your family.",
    nameTaken: "That name is taken.",
    familyNameTaken: "That family name is taken.",
    noRoom: "This account has no room for another character.",
    voiceInvalid: "That voice is not one to choose.",
    partyInvitesInvalid: "That is not a choice for party invitations.",
    lookInvalid: "That look is not one to choose.",
    lookFrozen: "A frozen character's look cannot be changed.",
    lingering: "Another of your characters is still in the world.",
    method: "Use POST.",
    contentType: "Send the body as application/json.",
    rate: "Too many requests.",
    body: "Expected a small JSON object.",
    route: "No such endpoint.",
    disabled: "This gateway serves no accounts.",
    unavailable: "The account service is unavailable.",
    origin: "That request came from a page this server does not serve.",
    maintenance: "The server is closed for maintenance.",
    banned: "This account is banned.",
    link: "That link has expired or was already used.",
    resetLimited: "Too many requests. Try again later.",
    currentPassword: "That is not your current password.",
    resendSoon: "A link was sent a moment ago. Try again in a minute.",
    resendLimited: "Too many links were sent. Try again later.",
  },
} as const;
